import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { currencyPreferenceApi } from '../api/currencyPreferences';
import { getActiveLocale } from '../i18n/i18n';
import type { IranCurrencyDisplayPolicy } from '../lib/currencyDisplay';
import { useDisplayPreferences } from '../lib/displayPreferences';

const fallbackPolicy: IranCurrencyDisplayPolicy = {
  mode: 'rial',
  rialsPerToman: null,
};

const CurrencyDisplayContext = createContext<IranCurrencyDisplayPolicy>(fallbackPolicy);

/**
 * Loads the tenant-owned denomination factor once and combines it with the operator's display-only
 * preference. Tenant switching clears the shared query cache before this provider reads the next policy.
 */
export function CurrencyDisplayProvider({
  children,
  enabled,
}: {
  children: ReactNode;
  enabled: boolean;
}) {
  const { iranCurrency } = useDisplayPreferences(getActiveLocale());
  const preference = useQuery({
    queryKey: ['currency-preference'],
    queryFn: currencyPreferenceApi.get,
    enabled,
  });

  const policy = useMemo<IranCurrencyDisplayPolicy>(() => ({
    mode: iranCurrency,
    rialsPerToman: enabled && preference.data && Number.isFinite(preference.data.rialsPerToman)
      && preference.data.rialsPerToman > 0
      ? preference.data.rialsPerToman
      : null,
  }), [enabled, iranCurrency, preference.data]);

  return <CurrencyDisplayContext.Provider value={policy}>{children}</CurrencyDisplayContext.Provider>;
}

export function useCurrencyDisplayPolicy(): IranCurrencyDisplayPolicy {
  return useContext(CurrencyDisplayContext);
}
